import { useState } from "react";

import { Button, Modal } from "@/components/ui";
import { GetHelpLink } from "@/components/support/GetHelpLink";
import { formatDateTime, formatMoney } from "@/lib/format";
import type { PaymentAttempt, PaymentDispute } from "@/lib/types";

/**
 * One line saying the customer's bank has queried a payment, with the details behind it in a dialog. Customers are
 * told that, when, and for how much, and what happens next: never how it stands, why, or the processor's ids (staff
 * follow it up; docs/payment-attempts.md). The wording is true whatever the outcome, since it is the same for all.
 */
export function DisputeNotice({ dispute, attempt }: { dispute: PaymentDispute; attempt: PaymentAttempt }) {
  const [open, setOpen] = useState(false);
  const money = (cents: number) => formatMoney(cents, dispute.currency);
  const rows: [string, string][] = [
    ["Payment", [money(attempt.amount_cents), attempt.payment_method_label].filter(Boolean).join(" · ")],
    ...(attempt.succeeded_at ? [["Paid on", formatDateTime(attempt.succeeded_at)] as [string, string]] : []),
    ["Your bank asked on", formatDateTime(dispute.opened_at)],
    ["Amount queried", money(dispute.amount_cents)],
  ];
  return (
    <li className="text-xs text-fg-muted">
      <span className="font-semibold text-fg">{dispute.notice}</span>
      {" · "}
      {formatDateTime(dispute.opened_at)}
      {" · "}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="font-semibold text-brand-500 hover:underline"
      >
        View details
      </button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Your bank has queried this payment"
        footer={
          <>
            <GetHelpLink
              context={{
                resourceType: "payment dispute",
                resourceId: dispute.id,
                reason: "A payment my bank queried",
              }}
              variant="brand"
              size="md"
            />
            <Button onClick={() => setOpen(false)}>Done</Button>
          </>
        }
      >
        <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
          {rows.map(([label, value]) => (
            <div key={label} className="contents">
              <dt className="text-fg-muted">{label}</dt>
              <dd className="text-right font-medium text-fg">{value}</dd>
            </div>
          ))}
        </dl>
        <div className="space-y-2 text-sm text-fg-muted">
          <p>
            Your bank or card issuer has asked about this payment. This is sometimes called a dispute or a
            chargeback. We handle it with the bank and our payment processor.
          </p>
          <p>
            <span className="font-semibold text-fg">You don&apos;t need to do anything now.</span> If we need
            something from you, we&apos;ll contact you at your billing email.
          </p>
          <p>
            If you raised this with your bank yourself, or you think it&apos;s a mistake, contact support and
            tell us the payment shown above.
          </p>
        </div>
      </Modal>
    </li>
  );
}
